/* ============================================================
   Market Dashboard v3 — Liability tab.
   Single-leg (straight) exposure split from parlay, leg-count
   distribution, and pairwise concentration.
   Takes { betType, setBetType, data } — data slices: liability, market.
   ============================================================ */
import React from 'react';
import { liClass, dvMoney, dvK } from './marketDashboardUtils';

function combineLine(ln, betType) {
  if (betType === 'straight') return { lo: ln.straight.lo, lu: ln.straight.lu, n: ln.straight.n };
  if (betType === 'parlay') return { lo: ln.parlay.lo, lu: ln.parlay.lu, n: ln.parlay.n };
  return { lo: ln.straight.lo + ln.parlay.lo, lu: ln.straight.lu + ln.parlay.lu, n: ln.straight.n + ln.parlay.n };
}

export default function LiabilityTab({ betType, setBetType, data }) {
  const { liability, market } = data;
  const lines = liability.lines?.length
    ? liability.lines
    : [{ line: market.line, quote: true, straight: { lo: 0, lu: 0, n: 0 }, parlay: { lo: 0, lu: 0, n: 0 } }];
  const legDist = liability.legDist?.length ? liability.legDist : [{ legs: 1, n: 0, fee: 0 }];
  const pairwise = liability.pairwise || [];
  const maxAbs = Math.max(
    1,
    ...lines.flatMap((l) => [Math.abs(l.straight.lo), Math.abs(l.straight.lu), Math.abs(l.parlay.lo), Math.abs(l.parlay.lu)])
  );
  const legMax = Math.max(1, ...legDist.map((d) => d.n));
  const pairMax = Math.max(1, ...pairwise.map((p) => p.joint));

  const quoteLine = lines.find((l) => l.quote) || lines[0];
  const qc = combineLine(quoteLine, betType);
  const worst = Math.abs(qc.lo) >= Math.abs(qc.lu) ? qc.lo : qc.lu;
  const totalLegs = legDist.reduce((s, d) => s + d.n, 0);
  const straightN = legDist.reduce((s, d) => s + (d.legs === 1 ? d.n : 0), 0);
  const straightShare = totalLegs > 0 ? straightN / totalLegs : 0;

  return (
    <div>
      <div className="callout">
        <span className="tag">New</span>
        <span>
          <strong>Single-leg bets</strong> are now booked separately from parlays. Straight exposure is exact per-outcome P&amp;L;
          parlay exposure is this leg's contribution embedded across 2–8 leg tickets. <strong>Pairwise concentration</strong> below shows
          which other legs this market rides with — the tractable cut when full joint exposure spans up to 2⁸ combinations.
        </span>
      </div>

      <div className="liab-toolbar">
        <div className="seg" role="tablist" aria-label="Bet type">
          <span className="seg-label">Bet type</span>
          {['all', 'straight', 'parlay'].map((t) => (
            <button key={t} className={`seg-btn${betType === t ? ' seg-btn--on' : ''}`} onClick={() => setBetType(t)}>
              {t === 'all' ? 'All' : t === 'straight' ? 'Straight (1-leg)' : 'Parlay (2–8)'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'baseline', fontSize: '0.72rem' }}>
          <span className="gv-muted">Quote-line worst case <strong className={`mono ${liClass(worst, maxAbs)}`} style={{ fontSize: '0.95rem', marginLeft: 6 }}>{dvMoney(worst, { sign: true })}</strong></span>
          <span className="gv-muted">Single-leg share <strong style={{ color: 'var(--straight)', marginLeft: 6 }}>{(straightShare * 100).toFixed(0)}%</strong></span>
        </div>
      </div>

      <div className="liab-grid">
        <div className="chart-card">
          <h3>Liability by line <span className="gv-muted" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '0.66rem' }}>straight vs parlay · O / U net</span></h3>
          <div className="liab-table-wrap">
            <table className="liab-table">
              <thead>
                <tr>
                  <th className="l" rowSpan="2">Line</th>
                  <th className="grp grp--straight colgap" colSpan="3">Straight (single-leg)</th>
                  <th className="grp grp--parlay colgap" colSpan="3">Parlay (2–8 legs)</th>
                  <th rowSpan="2" className="colgap">Net worst</th>
                </tr>
                <tr>
                  <th className="colgap">Li Over</th><th>Li Under</th><th>n</th>
                  <th className="colgap">Li Over</th><th>Li Under</th><th>n</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((ln) => {
                  const c = combineLine(ln, betType);
                  const w = Math.abs(c.lo) >= Math.abs(c.lu) ? c.lo : c.lu;
                  const dimS = betType === 'parlay', dimP = betType === 'straight';
                  return (
                    <tr key={ln.line} className={ln.quote ? 'liab-row-quote' : ''}>
                      <td className="l">
                        <span className="liab-line-label">{ln.line}</span>
                        {ln.quote && <span className="market-hero-line-pill" style={{ marginLeft: 6, fontSize: '0.6rem', padding: '0.05rem 0.4rem' }}>quote</span>}
                      </td>
                      <td className={`colgap mono ${liClass(ln.straight.lo, maxAbs)}`} style={dimS ? { opacity: 0.32 } : null}>{dvMoney(ln.straight.lo, { sign: true })}</td>
                      <td className={`mono ${liClass(ln.straight.lu, maxAbs)}`} style={dimS ? { opacity: 0.32 } : null}>{dvMoney(ln.straight.lu, { sign: true })}</td>
                      <td className="mono gv-muted" style={dimS ? { opacity: 0.32 } : null}>{ln.straight.n}</td>
                      <td className={`colgap mono ${liClass(ln.parlay.lo, maxAbs)}`} style={dimP ? { opacity: 0.32 } : null}>{dvMoney(ln.parlay.lo, { sign: true })}</td>
                      <td className={`mono ${liClass(ln.parlay.lu, maxAbs)}`} style={dimP ? { opacity: 0.32 } : null}>{dvMoney(ln.parlay.lu, { sign: true })}</td>
                      <td className="mono gv-muted" style={dimP ? { opacity: 0.32 } : null}>{ln.parlay.n}</td>
                      <td className={`colgap mono ${liClass(w, maxAbs)}`}>{dvMoney(w, { sign: true })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h3 style={{ marginTop: '1.1rem' }}>Leg-count distribution <span className="gv-muted" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '0.66rem' }}>where the entries sit</span></h3>
          <div className="legdist">
            {legDist.map((d) => (
              <div className="legdist-row" key={d.legs}>
                <span className="legdist-label">{d.legs} {d.legs === 1 ? 'leg' : 'legs'}{d.legs === 1 && <span className="tag">straight</span>}</span>
                <span className="legdist-track"><span className="legdist-fill" style={{ width: `${(d.n / legMax) * 100}%`, background: d.legs === 1 ? 'var(--straight)' : 'var(--parlay)' }} /></span>
                <span className="legdist-val">{d.n} · {dvK(d.fee)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="chart-card">
          <h3>Pairwise concentration <span className="gv-muted" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '0.66rem' }}>most parlayed with</span></h3>
          <div className="pair-intro">
            Joint liability riding on <strong>{market.player} {market.market_label} {market.line}</strong> together with each leg below.
            <span className="pair-leg-meta"> Lift = how much more often they co-occur than chance.</span>
          </div>
          {pairwise.length ? (
            pairwise.map((p, i) => (
              <div className="pair-row" key={i}>
                <div className="pair-leg">
                  <div className="pair-leg-sel">
                    <span className={`pair-leg-type pair-leg-type--${p.type}`}>{p.type === 'player' ? 'Player' : 'Mainline'}</span>
                    {p.sel}
                  </div>
                  <div className="pair-bar" style={{ width: `${(p.joint / pairMax) * 100}%` }} />
                </div>
                <div className="pair-right">
                  <div className="pair-joint li-pos">{dvMoney(p.joint)}</div>
                  <div className="pair-lift">{p.n} tickets · {p.lift.toFixed(1)}× lift</div>
                </div>
              </div>
            ))
          ) : (
            <div className="gv-muted" style={{ fontSize: '0.72rem', lineHeight: 1.5 }}>
              Pairwise parlay concentration is not available from the current API payload.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
